import type { Table } from '@tanstack/react-table'
import { memo } from 'react'

import {
  PaginationInfo,
  PaginationNavigation,
  PaginationPageSize,
} from '@/components/ui/pagination'
import { usePagination } from '@/hooks/use-pagination'

interface DataTablePaginationProps<TData> {
  table: Table<TData>
  pageSizeOptions?: number[]
  pagination: {
    total: number
    page: number
    per_page: number
    total_pages: number
  }
}

/**
 * Reusable pagination component for data tables
 */
function DataTablePaginationComponent<TData>({
  table,
  pageSizeOptions,
  pagination: serverPagination,
}: DataTablePaginationProps<TData>) {
  const pageSize = table.getState().pagination.pageSize
  const currentPage = table.getState().pagination.pageIndex + 1

  const totalItems = serverPagination.total
  const totalPages = serverPagination.total_pages

  const pagination = usePagination({
    currentPage,
    totalItems,
    pageSize,
    pageSizeOptions,
    onPageChange: (page) => table.setPageIndex(page - 1),
    onPageSizeChange: (size) => table.setPageSize(size),
  })

  return (
    <div className="flex items-center justify-between py-1">
      <PaginationInfo
        startRow={pagination.startRow}
        endRow={pagination.endRow}
        totalItems={totalItems}
        currentPage={currentPage}
        totalPages={totalPages}
      />
      <div className="flex items-center space-x-6 lg:space-x-8">
        <PaginationPageSize
          pageSize={pageSize}
          onPageSizeChange={(size) => table.setPageSize(size)}
          pageSizeOptions={pageSizeOptions}
        />
        <PaginationNavigation
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={(page) => table.setPageIndex(page - 1)}
          canGoPrevious={pagination.canGoPrevious}
          canGoNext={pagination.canGoNext}
          goToFirst={pagination.goToFirst}
          goToPrevious={pagination.goToPrevious}
          goToNext={pagination.goToNext}
          goToLast={pagination.goToLast}
        />
      </div>
    </div>
  )
}

export const DataTablePagination = memo(
  DataTablePaginationComponent,
) as typeof DataTablePaginationComponent
